// Senvè Welcome Offer popup.
//
// The email form is a real Shopify native `customer` form and submits as a
// normal full-page POST (see the header comment in
// snippets/welcome-offer.liquid for why - this store's customer forms
// require solving a visible hCaptcha challenge, and intercepting the
// submit with fetch() skips that entirely and always fails). This script
// only handles: the popup chrome (open/close/focus/backdrop), the
// suppression, and auto-opening already in its success (or
// validation-error) state after a real form submission redirects back
// here.
//
// Trigger: opens immediately on page load. Only on home/product/collection
// templates (enforced both server-side in theme.liquid and here via
// data-template).
// Suppression (all via localStorage, no cookies): closing suppresses for
// 7 days; a successful signup suppresses permanently; never shows while
// the cart drawer is open, and opening the cart drawer while the popup is
// open closes it. Coexists with the mobile sticky Add to Cart via a CSS
// rule (body.senve-welcome-offer-open hides it) rather than touching the
// sticky CTA's own logic.
(() => {
  const root = document.getElementById('senve-welcome-offer');
  if (!root) return;

  const STORAGE_KEY = 'senve:welcome-offer';
  const SUPPRESS_DAYS = 7;
  const justSubscribed = root.dataset.justSubscribed === 'true';
  const hasErrors = root.dataset.hasErrors === 'true';
  const autoOpen = justSubscribed || hasErrors;

  const readState = () => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    } catch (e) {
      return {};
    }
  };
  const writeState = (patch) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readState(), ...patch }));
    } catch (e) {
      /* localStorage unavailable (private browsing etc.) - popup just won't persist suppression */
    }
  };

  const state = readState();

  if (justSubscribed) {
    writeState({ subscribed: true });
  } else if (!hasErrors) {
    if (state.subscribed) return;
    if (state.dismissedUntil && Date.now() < state.dismissedUntil) return;

    const allowedTemplates = ['index', 'product', 'collection'];
    const currentTemplate = document.body.dataset.senveTemplate || '';
    if (!allowedTemplates.includes(currentTemplate)) return;
  }

  const backdrop = root.querySelector('[data-welcome-dismiss].senve-welcome-offer__backdrop');
  const panel = root.querySelector('.senve-welcome-offer__panel');
  const closeBtn = root.querySelector('.senve-welcome-offer__close');
  const successState = root.querySelector('[data-welcome-success]');
  const codeText = root.querySelector('[data-welcome-code-text]');
  const copyBtn = root.querySelector('[data-welcome-copy]');
  const cartDrawer = document.querySelector('cart-drawer');

  let opened = false;
  let previouslyFocused = null;
  let closeTimer = null;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // The cart drawer's own open sequence adds `overflow-hidden` to <body>
  // synchronously, before the `active` class lands on the drawer a frame
  // later (see cart-drawer.js) - checking both closes that timing gap
  // instead of only reacting after the drawer has finished opening.
  const cartIsOpen = () =>
    (!!cartDrawer && cartDrawer.classList.contains('active')) ||
    document.body.classList.contains('overflow-hidden');

  const getFocusable = () =>
    Array.from(
      panel.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null);

  // Containment via `focusin` (fires on the element that gains focus,
  // bubbles) rather than only intercepting Tab: this also catches focus
  // moving outside the panel programmatically, not just via keyboard.
  const onFocusIn = (event) => {
    if (panel.contains(event.target)) return;
    const focusable = getFocusable();
    (focusable[0] || panel).focus();
  };

  const onKeydown = (event) => {
    if (event.key === 'Escape') close();
  };

  function open() {
    if (opened || (cartIsOpen() && !autoOpen)) return;
    if (closeTimer) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }
    opened = true;
    previouslyFocused = document.activeElement;
    root.hidden = false;
    root.setAttribute('data-state', 'entering');
    panel.setAttribute('aria-hidden', 'false');
    document.body.classList.add('senve-welcome-offer-open');
    document.body.style.overflow = 'hidden';
    // Force layout so the entering transform/opacity actually transitions
    // instead of jumping straight to the open state.
    // eslint-disable-next-line no-unused-expressions
    root.offsetHeight;
    root.removeAttribute('data-state');
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('focusin', onFocusIn);
    const focusTarget = getFocusable()[0];
    (focusTarget || panel).focus();
  }

  function close() {
    if (!opened) return;
    opened = false;
    root.setAttribute('data-state', 'closed');
    panel.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('senve-welcome-offer-open');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onKeydown);
    document.removeEventListener('focusin', onFocusIn);
    closeTimer = window.setTimeout(
      () => {
        root.hidden = true;
        root.removeAttribute('data-state');
        closeTimer = null;
      },
      reducedMotion() ? 0 : 250
    );
    if (previouslyFocused && document.body.contains(previouslyFocused) && previouslyFocused !== document.body) {
      previouslyFocused.focus();
    } else {
      const main = document.getElementById('MainContent');
      if (main) main.focus();
    }
    if (!successState) {
      writeState({ dismissedUntil: Date.now() + SUPPRESS_DAYS * 24 * 60 * 60 * 1000 });
    }
  }

  [backdrop, closeBtn].forEach((el) => el.addEventListener('click', () => close()));

  if (copyBtn) {
    copyBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(codeText.textContent.trim());
        copyBtn.textContent = 'Copied';
        copyBtn.dataset.copied = 'true';
        window.setTimeout(() => {
          copyBtn.textContent = 'Copy';
          delete copyBtn.dataset.copied;
        }, 2000);
      } catch (err) {
        /* clipboard API unavailable - the code is already selectable as plain text */
      }
    });
  }

  // Cart drawer coexistence: never open while the cart is open, and close
  // immediately if the visitor opens the cart while the popup is showing.
  if (cartDrawer) {
    new MutationObserver(() => {
      if (cartIsOpen() && opened) close();
    }).observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
    new MutationObserver(() => {
      if (cartIsOpen() && opened) close();
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  if (autoOpen) {
    // Skip the immediate-open check entirely - the visitor just completed
    // a real form submission (successful or not), so show the result
    // right away.
    open();
    return;
  }

  function triggerImmediately() {
    if (!cartIsOpen()) open();
  }

  if (document.readyState === 'complete') triggerImmediately();
  else window.addEventListener('load', triggerImmediately);
})();
